import Phaser from 'phaser';
import { BGM, SFX, BgmKey, SfxKey } from './AudioKeys';
import { MASTER_VOLUME_DEFAULT, MUSIC_VOLUME_DEFAULT, SFX_VOLUME_DEFAULT, RATE_LIMIT_MS, CROSSFADE_MS_DEFAULT, clamp01 } from './AudioConfig';
import { loadMuted, saveMuted, loadVolume, saveVolume } from './Persistence';
import type { PlayMusicOptions, PlaySfxOptions } from './types';

/**
 * Scene-aware audio manager built on top of Phaser's Sound Plugin.
 * Guarded for missing assets: attempts to play unknown keys are no-ops.
 */
export class SoundManager {
  private scene: Phaser.Scene | null = null;
  private snd!: Phaser.Sound.BaseSoundManager;
  private music?: Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound; // single active music instance
  private lastPlayedAt: Record<string, number> = Object.create(null);

  // Cached volumes
  private masterVolume: number = MASTER_VOLUME_DEFAULT;
  private musicVolume: number = MUSIC_VOLUME_DEFAULT;
  private sfxVolume: number = SFX_VOLUME_DEFAULT;

  initFromScene(scene: Phaser.Scene): void {
    this.scene = scene;
    this.snd = scene.sound;

    // Apply persisted prefs
    const persistedVol = loadVolume();
    const persistedMute = loadMuted();
    if (typeof persistedVol === 'number') {
      this.setMasterVolume(persistedVol);
    } else {
      this.setMasterVolume(MASTER_VOLUME_DEFAULT);
    }
    this.setMuted(persistedMute);

    // Ensure sound manager honors master volume and mute
    this.applyMasterVolume();
  }

  // ----------------
  // Volume / Mute
  // ----------------
  setMasterVolume(v01: number): void {
    this.masterVolume = clamp01(v01);
    saveVolume(this.masterVolume);
    this.applyMasterVolume();
  }

  getMasterVolume(): number {
    return this.masterVolume;
  }

  setMuted(muted: boolean): void {
    if (!this.snd) return;
    this.snd.mute = !!muted;
    saveMuted(!!muted);
  }

  toggleMute(): boolean {
    const muted = !this.isMuted();
    this.setMuted(muted);
    return muted;
  }

  isMuted(): boolean {
    return !!this.snd?.mute;
  }

  private applyMasterVolume(): void {
    if (!this.snd) return;
    this.snd.volume = this.masterVolume;
    // Adjust current music base volume to respect configured musicVolume
    if (this.music) {
      this.music.setVolume(this.musicVolume * this.masterVolume);
    }
  }

  setMusicVolume(v01: number): void {
    this.musicVolume = clamp01(v01);
    if (this.music) {
      this.music.setVolume(this.musicVolume * this.masterVolume);
    }
  }

  setSfxVolume(v01: number): void {
    this.sfxVolume = clamp01(v01);
  }

  // ----------------
  // Music
  // ----------------
  playMusic(key: BgmKey, opts: PlayMusicOptions = {}): void {
    if (!this.scene || !this.snd) return;
    if (!this.hasAudioKey(key)) return;

    // If same track already playing, just ensure loop/volume
    if (this.music && this.music.key === key) {
      if (!this.music.isPlaying) {
        this.music.play({ loop: opts.loop ?? true });
      }
      this.music.setLoop(opts.loop ?? true);
      if (typeof opts.volume === 'number') {
        this.setMusicVolume(opts.volume);
      }
      return;
    }

    // If different track, crossfade
    const fadeMs = opts.fadeInMs ?? CROSSFADE_MS_DEFAULT;
    this.crossfadeTo(key, fadeMs, { loop: opts.loop ?? true, volume: opts.volume, detune: opts.detune, rate: opts.rate });
  }

  crossfadeTo(key: BgmKey, durationMs: number = CROSSFADE_MS_DEFAULT, opts?: PlayMusicOptions): void {
    if (!this.scene || !this.snd) return;
    if (!this.hasAudioKey(key)) return;

    const next = this.snd.add(key, { loop: opts?.loop ?? true, detune: opts?.detune, rate: opts?.rate }) as Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound;
    const targetVol = clamp01(typeof opts?.volume === 'number' ? opts!.volume! : this.musicVolume);

    // Start silent, fade in
    next.play({ volume: 0 });

    const start = this.scene.time.now;
    const end = start + Math.max(0, durationMs);

    const updateFade = () => {
      const now = this.scene!.time.now;
      const t = Math.max(0, Math.min(1, (now - start) / Math.max(1, end - start)));
      const vol = Phaser.Math.Linear(0, targetVol * this.masterVolume, t);
      next.setVolume(vol);

      if (t >= 1) {
        this.scene!.events.off(Phaser.Scenes.Events.POST_UPDATE, updateFade);
        // Stop previous after fade completes
        if (this.music) {
          try { this.music.stop(); } catch {}
          try { this.music.destroy(); } catch {}
        }
        this.music = next;
      }
    };

    // Kick off tween-like fade via POST_UPDATE
    this.scene.events.on(Phaser.Scenes.Events.POST_UPDATE, updateFade);

    // Ensure previous music starts fading out immediately
    if (this.music) {
      const prev = this.music;
      const prevStart = this.scene.time.now;
      const prevEnd = prevStart + Math.max(0, durationMs);
      const startVol = typeof (prev as any).volume === 'number' ? (prev as any).volume as number : this.musicVolume * this.masterVolume;
      const fadeOut = () => {
        const now = this.scene!.time.now;
        const t = Math.max(0, Math.min(1, (now - prevStart) / Math.max(1, prevEnd - prevStart)));
        const vol = Phaser.Math.Linear(startVol, 0, t);
        prev.setVolume(vol);
        if (t >= 1) {
          this.scene!.events.off(Phaser.Scenes.Events.POST_UPDATE, fadeOut);
        }
      };
      this.scene.events.on(Phaser.Scenes.Events.POST_UPDATE, fadeOut);
    } else {
      // If no previous, set reference immediately at end of fade through update hook
      this.music = next;
    }
  }

  stopMusic(fadeMs: number = 0): void {
    if (!this.scene || !this.music) return;
    if (fadeMs <= 0) {
      try { this.music.stop(); } catch {}
      try { this.music.destroy(); } catch {}
      this.music = undefined;
      return;
    }
    const m = this.music;
    const start = this.scene.time.now;
    const end = start + fadeMs;
    const startVol = typeof (m as any).volume === 'number' ? (m as any).volume as number : this.musicVolume * this.masterVolume;
    const fadeOut = () => {
      const now = this.scene!.time.now;
      const t = Math.max(0, Math.min(1, (now - start) / Math.max(1, end - start)));
      const vol = Phaser.Math.Linear(startVol, 0, t);
      m.setVolume(vol);
      if (t >= 1) {
        this.scene!.events.off(Phaser.Scenes.Events.POST_UPDATE, fadeOut);
        try { m.stop(); } catch {}
        try { m.destroy(); } catch {}
        if (this.music === m) this.music = undefined;
      }
    };
    this.scene.events.on(Phaser.Scenes.Events.POST_UPDATE, fadeOut);
  }

  // ----------------
  // SFX
  // ----------------
  playSfx(key: SfxKey, opts: PlaySfxOptions = {}, rateLimitKey?: string): void {
    if (!this.scene || !this.snd) return;
    if (!this.hasAudioKey(key)) return;

    // Rate limiting
    const now = performance.now();
    const bucket = rateLimitKey ?? key;
    const last = this.lastPlayedAt[bucket] ?? -Infinity;
    const minInterval = this.getMinIntervalFor(bucket);
    if (now - last < minInterval) return;
    this.lastPlayedAt[bucket] = now;

    const volume = clamp01((typeof opts.volume === 'number' ? opts.volume : this.sfxVolume) * this.masterVolume);
    try {
      const s = this.snd.add(key) as Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound;
      s.play({ volume, detune: opts.detune, rate: opts.rate });
      // destroy when finished
      s.once('complete', () => { try { s.destroy(); } catch {} });
    } catch {
      // ignore if sound manager cannot create
    }
  }

  // Optional spatial SFX helper (kept simple; safe when key missing)
  playSfxSpatial(key: SfxKey, x: number, y: number, follow?: Phaser.GameObjects.GameObject, refDistance: number = 50): void {
    if (!this.scene || !this.snd) return;
    if (!this.hasAudioKey(key)) return;

    const volume = clamp01(this.sfxVolume * this.masterVolume);
    try {
      const s = this.snd.add(key) as Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound;
      s.play({
        volume,
        source: { x, y, refDistance, follow }
      } as any);
      s.once('complete', () => { try { s.destroy(); } catch {} });
    } catch {
      // ignore
    }
  }

  setListenerPosition(x: number, y: number): void {
    if (!this.snd) return;
    try {
      (this.snd as any).setListenerPosition?.(x, y);
    } catch {
      // not supported in HTML5 audio; safe no-op
    }
  }

  // ----------------
  // Helpers
  // ----------------
  private getMinIntervalFor(bucket: string): number {
    switch (bucket) {
      case 'shoot':
      case SFX.Shoot:
        return RATE_LIMIT_MS.SHOOT;
      case 'ui_click':
      case SFX.UiClick:
        return RATE_LIMIT_MS.UI_CLICK;
      default:
        return 0;
    }
  }

  private hasAudioKey(key: string): boolean {
    // Prefer Phaser's cache API and avoid backend-specific introspection.
    try {
      const sceneCache = (this.scene as any)?.cache?.audio;
      if (sceneCache?.has?.(key)) {
        return true; // Trust Phaser cache presence
      }

      // Fallback to older path if needed
      const legacyCache = (this.snd as any)?.game?.cache?.audio;
      if (legacyCache?.has?.(key)) {
        return true;
      }

      // As a last resort, attempt a very lenient inspection without requiring audioBuffer
      if (legacyCache) {
        const entry = legacyCache.get?.(key);
        if (entry) {
          const dataArr = (entry as any).data;
          const source = (entry as any).source;
          if ((Array.isArray(dataArr) && dataArr.length > 0) || !!dataArr || !!source) {
            return true;
          }
        }
      }
      return false;
    } catch {
      return false;
    }
  }
}

export default SoundManager;